/**
 * "Find my size" tile — snippets/truefit-size-tile.liquid
 *
 * True Fit is not downloaded on page load any more. When the tile is tapped this
 * script:
 *   1. loads the True Fit library through window.AetrexTrueFit.load() (layout/theme.liquid),
 *   2. waits for True Fit to render its widget into the app block's .tfc-fitrec-product
 *      placeholder (the block is collapsed by CSS, not removed),
 *   3. opens the fit finder,
 *   4. mirrors whatever the widget says, so the collapsed widget is never missed. A
 *      recommendation: on desktop the capsule itself shows the size ("Recommended:
 *      9 - 9.5 US", just the size when the label row is too tight) and the size box
 *      gets no visible mark (its label carries "Recommended for you" for screen readers
 *      only); on mobile the bar turns into "Update my size" over "True Fit recommends
 *      9 - 9.5 US" and the matching dropdown row gets a "Recommended for you" pill
 *      (shortened to fit). Any other line ("Many shoppers tend to size down", "Based on
 *      581 purchases", a no-data note) becomes the bar's second line, in True Fit's own
 *      words. The desktop capsule has one line, so there that text is screen-reader only.
 *
 * Shoppers True Fit already knows (a recommendation was shown to them before, or True
 * Fit's own token cookie is present) get their size marked on every product without a
 * tap, as soon as the background load has run.
 *
 * True Fit serves two runtimes and picks one per shopper (tf-version cookie):
 *   - v1 renders an <iframe> and exposes tfcapi('triggerOpen', { styleId }).
 *   - v2 (what Aetrex shoppers get today) renders a <tf-recommendation> web component
 *     with an open shadow root, and its tfcapi('triggerOpen') is a no-op. The tile
 *     clicks the component's own "What's my size?" control instead. v2 mounts its
 *     dialog in a shadow root on <body> (a zero-size inline host, so the host itself is
 *     never "visible"). The widget does not draw into a display:none element, so the
 *     collapsed block keeps real layout and is merely invisible (CSS) and inert (here).
 *
 * Waiting is driven by evidence, not a stopwatch: True Fit's own network activity
 * (seen through PerformanceObserver) counts as progress, so a cold or contended load
 * gets as long as it needs (up to a hard cap). The tap also asks the theme's
 * deferred-script loaders to hold the analytics storm for a few seconds so True Fit
 * has the network to itself, and starts loading on pointerdown, a beat before click.
 *
 * The widget is drawn into a block that is laid out but invisible. If True Fit will
 * not draw into it (its library is in, yet nothing appears for a moment), the block is
 * revealed in its natural place under the size picker just long enough for the widget
 * to render and be opened, then hidden again, so a tap always ends in the finder
 * whatever visibility rule True Fit applies. The same brief reveal reads the result
 * after the finder closes when the hidden widget stays blank.
 *
 * True Fit's component takes its store from its own store-key attribute. On this page a
 * second True Fit integration (storeKey=atx) was loading alongside ours and mounting the
 * component with its key; every request then went to store "atx", the first was refused
 * (CORS) and the widget stayed blank. delay-third-party-scripts now holds any True Fit
 * script for a store key other than ours; as a second line, a component found wearing a
 * foreign key is replaced by one wearing ours before anything else is tried.
 *
 * While the finder is open, True Fit locks the page behind it with overflow:hidden on
 * <body>. On this theme <html> clips horizontally, so that lock never reaches the
 * viewport: it lands on <body> itself, the page still scrolls, and every position:sticky
 * element in the page — the product column, the pinned announcement bar, the header —
 * loses its footing and jumps (the bar scrolls out of sight, the product column goes
 * blank), then jumps back on close. Two guards keep the page still, from the first frame
 * the lock is set, for every sticky element on the page: while an overflow lock is on
 * <body>, <html> is let go to overflow:visible so the lock is applied to the viewport as
 * intended (sticky positioning intact, <body>'s own style untouched so the lock's removal
 * is seen); and any sticky element that still moves — under a body class or a frozen
 * <body> — is slid back to where it was (no layout change), the slide re-measured on
 * every pass and dropped as soon as the element stands on its own. A page scroll True
 * Fit performs while opening is undone too. Everything is handed back on close.
 *
 * True Fit is also warmed up in the background once the page has settled (WARM_AFTER_IDLE),
 * so a tap a few seconds into the visit opens the finder at once. Set it to false to go
 * back to loading nothing from True Fit until the first tap.
 *
 * What happens when it does not work out — never a surprise size chart:
 *   - the loader fails (blocked, offline) → definitive: the tile becomes a size chart
 *     shortcut and opens it this once;
 *   - True Fit answers "nothing for this style" (v1 explicitly; v2 by rendering text with
 *     no control while visible) → definitive: the same;
 *   - True Fit stays silent or slow → the tile goes back to rest reading "Try again" and
 *     opens nothing; a second silent attempt downgrades it to the size chart shortcut;
 *   - the shopper taps something else while waiting → the attempt is cancelled quietly;
 *   - the finder cannot be seen after the attempt → the tile simply returns to rest; the
 *     block stays armed a while longer in case the finder is open but undetected.
 */
(() => {
  'use strict';

  if (window.AetrexTrueFitTile) return;

  const TILE = '[data-tf-size-tile]';
  const TILE_ITEM = '[data-tf-size-tile-item]';
  const PLACEHOLDER = '.tfc-fitrec-product';
  const V2_TAG = 'tf-recommendation';
  // Inside the widget component anything modal-shaped counts; on <body> only real dialogs do.
  const V2_DIALOG = '[role="dialog"], [role="presentation"], [aria-modal="true"], .MuiDialog-root, .MuiModal-root, .MuiDrawer-root, .MuiPopover-root';
  const BODY_DIALOG = '[role="dialog"], [aria-modal="true"], .MuiDialog-root, .MuiModal-root, .MuiDrawer-root';
  const TF_URL = /truefit/i;
  const CTA_TEXT = /my size|find .*size|size\?/i;

  const HARD_CAP = 25000;       // absolute ceiling on a single attempt
  const STALL_QUIET = 8000;     // no True Fit network activity for this long and nothing rendered → stalled
  const EMPTY_QUIET = 5000;     // component present without text, and quiet for this long → no fit data (probable)
  const EMPTY_MIN_AGE = 3000;   // ...but only once the component has existed at least this long
  const CONTENT_MIN_AGE = 2500; // component shows text but no control for this long...
  const CONTENT_QUIET = 4000;   // ...and True Fit has been quiet this long, while visible → no fit data
  const NUDGE_AFTER = 1200;     // library just loaded, no control drawn for this long → start nudging the widget
  const NUDGE_AFTER_WARM = 200; // library was already in before the tap (warm-up) → start almost at once
  const NUDGE_QUIET = 400;      // ...but only while True Fit is not mid-request
  const NUDGE_STEP = 600;       // between nudges: re-attach hidden → reveal → re-attach visible
  const RECALC_AFTER = 1500;    // re-attached visible and still blank for this long → fresh component via tfcapi('calculate')
  const PEEK_FOR = 4000;        // after the finder closes, how long a blank widget is revealed to read it
  const V1_ANSWER_GRACE = 1500; // v1 answered but drew nothing
  const READY_WATCH = 60000;    // after a failed attempt, how long to keep watching for the widget to turn up
  const OPEN_TIMEOUT = 4000;    // after the open attempt, how long the finder gets to appear
  const UNDETECTED_ARMED = 45000; // if it never appears, how long the block stays armed anyway
  const LOADING_LABEL_AFTER = 1800;
  const HOLD_THIRD_PARTY = 4000;
  const CLOSE_POLL = 400;
  const MIN_DIALOG_SIZE = 200;  // px; a real finder dialog is far bigger than any tooltip or popover
  const WARM_AFTER_IDLE = true;  // load True Fit in the background for everyone once the page has settled
  const WARM_DELAY = 2500;       // ...this long after the load event
  const PROFILE_KEY = 'tfTileProfile';
  const SIZE_INPUT = 'li.custom-list-items[data-option*="size"] input[type="radio"]';
  const SIZE_OPTION = 'size-dropdown .size-dropdown__option[data-value]';

  const state = {
    busy: false,
    loadingLabel: false,
    unavailable: false,
    answered: false,
    listening: false,
    lastTile: null,
    modalWasOpen: false,
    armedBlock: null,
    closeTimer: 0,
    lastActivity: 0,
    attempt: 0,
    attemptStartedAt: 0,
    quietStrikes: 0,
    nodataStrikes: 0,
    readyTimer: 0,
    widgetResult: null,
    recommendation: null,
    insight: [],
    trace: [],
    resources: [],
    inflight: 0,
    pick: null,
    known: false,
    retry: false,
    revealed: false,
    finderOpen: false,
    opening: false,
    lockRelayed: null,
    stickyBefore: null,
    scrollBefore: null,
    block: null,
    productKey: null,
  };
  let debug = false;
  try { debug = sessionStorage.getItem('tfTileDebug') === '1'; } catch (error) { /* storage blocked */ }

  const tiles = () => Array.from(document.querySelectorAll(TILE));
  // No tile on this page (a template without the True Fit block, or a size picker that does
  // not render it): nothing below runs. No True Fit load, no block hidden or made inert, no
  // listeners on the page. Whatever the template shows in the True Fit block's place is
  // left exactly as the theme rendered it.
  if (!tiles().length) return;
  const placeholder = () => document.querySelector(PLACEHOLDER);
  const sizeChartButton = () => document.querySelector('button[aria-controls^="SizeChart-"]');
  const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
  const squash = (text) => String(text || '').replace(/\s+/g, ' ').trim();

  // kind: 'timeout' (slow or stalled, try again) | 'nodata' (definitive) | 'quiet'
  // (probable no data) | 'failed' (loader blocked or errored) | 'cancelled'.
  class TileError extends Error {
    constructor(kind, detail) {
      super('truefit-size-tile: ' + kind);
      this.kind = kind;
      this.detail = detail || null;
    }
  }

  // Every decision the tile makes is recorded (window.AetrexTrueFitTile.dump() prints
  // the lot), so a wrong turn on a real page can be read back instead of guessed at.
  function trace(event, data) {
    const entry = Object.assign({ t: Date.now() - (state.attemptStartedAt || Date.now()), event }, data || {});
    state.trace.push(entry);
    if (state.trace.length > 300) state.trace.shift();
    if (debug) console.log('[tf-tile]', entry);
  }

  // What the page looks like from True Fit's side: the component's attributes and
  // geometry, the block's computed style, the <body>-level shadow hosts True Fit draws
  // its finder into (and may draw other things into), and the source of its API
  // dispatcher and custom element. String literals survive minification, so the
  // commands the API accepts and the checks the element makes can be read off.
  function probe() {
    const safe = (fn, fallback) => { try { return fn(); } catch (error) { return fallback === undefined ? String(error) : fallback; } };
    const rectOf = (el) => safe(() => { const r = el.getBoundingClientRect(); return [Math.round(r.left), Math.round(r.top), Math.round(r.width), Math.round(r.height)]; }, null);
    const styleOf = (el) => safe(() => { const s = getComputedStyle(el); return { display: s.display, position: s.position, opacity: s.opacity, visibility: s.visibility, clipPath: s.clipPath, zIndex: s.zIndex }; }, null);
    const component = v2Component();
    const block = blockOf(placeholder());
    const hosts = [];
    safe(() => {
      for (const node of document.body.children) {
        if (!node.shadowRoot || node.localName === V2_TAG) continue;
        const root = node.shadowRoot;
        hosts.push({ tag: node.localName, id: node.id || null, len: root.innerHTML.length, text: visibleText(root).slice(0, 160), controls: root.querySelectorAll('button, a, [role="button"]').length, dialog: !!root.querySelector(BODY_DIALOG), size: rectOf(node) });
      }
    });
    const tfElements = safe(() => Array.from(document.querySelectorAll('*')).filter((el) => el.localName.startsWith('tf-')).map((el) => el.localName + (customElements.get(el.localName) ? '' : ' (undefined)')).slice(0, 20), []);
    // Every True Fit script tag on the page, with where it sits: a loader inside the app
    // block's wrapper is the app's, one in <head> came from a loader or the theme.
    const scripts = safe(() => Array.from(document.querySelectorAll('script')).filter((el) => TF_URL.test(el.src || el.getAttribute('data-delayed-src') || '')).map((el) => ({
      src: (el.src || el.getAttribute('data-delayed-src') || '').replace(/^https?:\/\//, '').slice(0, 120), held: el.getAttribute('data-delayed-src') ? (el.getAttribute('data-delayed-key') || 'yes') : null, type: el.type || null, async: el.async, defer: el.defer,
      parent: el.parentNode ? (el.parentNode.localName + (el.parentNode.id ? '#' + el.parentNode.id : '') + (el.parentNode.className && typeof el.parentNode.className === 'string' ? '.' + el.parentNode.className.trim().split(/\s+/).slice(0, 2).join('.') : '')) : null,
      block: !!el.closest('.shopify-app-block, .shopify-block, [id^="shopify-block"]'),
    })).slice(0, 20), []);
    const definition = safe(() => customElements.get(V2_TAG), null);
    return {
      component: component ? {
        attrs: safe(() => Array.from(component.attributes).map((a) => a.name + '=' + String(a.value).slice(0, 80)), []),
        rect: rectOf(component), style: styleOf(component), rawLength: safe(() => v2Root(component).innerHTML.length, null), styles: safe(() => v2Root(component).querySelectorAll('style').length, null),
        head: safe(() => v2Root(component).innerHTML.slice(0, 600), null),
      } : null,
      block: block ? { rect: rectOf(block), style: styleOf(block), classes: String(block.className).slice(0, 200), inert: block.hasAttribute('inert') } : null,
      viewport: [window.innerWidth, window.innerHeight, Math.round(window.scrollY)],
      layout: layoutSnapshot(),
      page: { finderOpen: state.finderOpen, opening: state.opening, armed: !!state.armedBlock, lock: safe(() => bodyLock(), null), relayed: !!state.lockRelayed, slideProp: SLIDE_PROP,
        sticky: state.stickyBefore ? state.stickyBefore.map((entry) => ({ el: labelOf(entry.el), before: Math.round(entry.top), now: rectOf(entry.el), held: entry.held ? entry.held.dy : null })).slice(0, 20) : null },
      hosts: hosts.slice(0, 12),
      tfElements,
      scripts,
      storeKey: STORE_KEY(),
      apiKeys: safe(() => window.tfcapi ? Object.keys(window.tfcapi).slice(0, 30) : null, null),
      sources: {
        api: safe(() => typeof window.tfcapi === 'function' ? String(window.tfcapi).slice(0, 8000) : null, null),
        element: definition ? safe(() => String(definition).slice(0, 12000), null) : null,
      },
    };
  }

  function dump() {
    const component = v2Component();
    const html = component ? v2Root(component).innerHTML.replace(/<style[\s\S]*?<\/style>/g, '').slice(0, 2000) : null;
    let probed = null;
    try { probed = probe(); } catch (error) { probed = String(error); }
    return JSON.stringify({
      tile: { busy: state.busy, unavailable: state.unavailable, retry: state.retry, revealed: state.revealed, recommendation: state.recommendation, pick: state.pick && state.pick.value, known: state.known, insight: state.insight, quietStrikes: state.quietStrikes, nodataStrikes: state.nodataStrikes, attempt: state.attempt, inflight: state.inflight },
      deferred: {
        held: window.__BSD ? { blocked: (window.__BSD.blocked || []).slice(0, 40), pending: (window.__BSD.pending || []).length, activated: window.__BSD.activated } : null,
        delayed: window.__delayedScripts ? window.__delayedScripts.pending().map((el) => el.getAttribute('data-delayed-src')) : null,
      },
      tfcapi: { type: typeof window.tfcapi, loaded: !!(window.tfcapi && window.tfcapi.tfIntegrationLoaded), queued: window.tfcapi && window.tfcapi.q ? window.tfcapi.q.length : null },
      placeholder: !!placeholder(),
      component: component ? { rendered: !!component.shadowRoot, html } : null,
      probe: probed,
      resources: state.resources,
      trace: state.trace,
    }, null, 1);
  }

  async function waitFor(test, timeout, step = 120) {
    const end = Date.now() + timeout;
    for (;;) {
      const value = test();
      if (value) return value;
      if (Date.now() >= end) return null;
      await wait(step);
    }
  }

  /* ---- Screen reader status ------------------------------------------------ */

  let liveRegion = null;

  function announce(message) {
    if (!liveRegion) {
      liveRegion = document.createElement('div');
      liveRegion.className = 'sr-only';
      liveRegion.setAttribute('aria-live', 'polite');
      liveRegion.setAttribute('aria-atomic', 'true');
      document.body.appendChild(liveRegion);
    }
    liveRegion.textContent = '';
    setTimeout(() => { liveRegion.textContent = message; }, 30);
  }

  /* ---- Tile rendering ------------------------------------------------------- */

  // The tile is a pure function of `state`: label = recommendation or the idle label;
  // subline = loading / result caption / size-chart fallback / True Fit's own insight /
  // the idle caption, in that order of precedence. The Liquid-rendered text is kept as
  // the idle copy the first time a tile is rendered.
  function render() {
    tiles().forEach((tile) => {
      const label = tile.querySelector('.tf-size-tile__label');
      const sub = tile.querySelector('[data-tf-sub]');
      if (!label || !sub) return;
      if (!tile.dataset.idleLabel) tile.dataset.idleLabel = squash(label.textContent);
      if (!tile.dataset.idleText) tile.dataset.idleText = squash(sub.textContent);

      const rec = state.recommendation;
      const insight = !rec && !state.unavailable ? state.insight : [];
      const inline = tile.classList.contains('tf-size-tile--inline');
      const retry = state.retry && !rec && !state.unavailable && !state.busy;
      const action = tile.dataset.resultLabel || 'Update my size';
      let labelText = rec ? action : tile.dataset.idleLabel;
      let sizeForm = -1;
      // The capsule has no second line, so its label carries "One moment…" and "Try again".
      if (inline && state.busy && state.loadingLabel) labelText = tile.dataset.loadingText || 'One moment…';
      else if (inline && retry) labelText = tile.dataset.retryLabel || 'Try again';
      // Once a size is known the capsule shows the size itself; the action moves to the
      // screen-reader line and the hover title. The mobile bar keeps the action as its label.
      else if (inline && rec) { const forms = inlineResultForms(tile, rec); sizeForm = fitLabel(label, forms); labelText = forms[sizeForm]; }
      if (label.textContent !== labelText) label.textContent = labelText;

      let text;
      let full = '';
      if (state.busy && state.loadingLabel) text = tile.dataset.loadingText || 'One moment…';
      else if (sizeForm >= 0) text = (sizeForm === 0 ? 'by True Fit. ' : 'recommended by True Fit. ') + action;
      else if (rec) text = fitResult(sub, [tile.dataset.resultText || 'True Fit recommends {size}', 'Recommended: {size}', '{size}'].map((t) => t.replace('{size}', rec)));
      else if (state.unavailable) text = tile.dataset.fallbackText || 'See the size chart';
      else if (retry) text = tile.dataset.retryText || 'True Fit did not answer. Tap to try again.';
      else if (insight.length) { full = insight.join(' · '); text = fitInsight(sub, insight); }
      else text = tile.dataset.idleText;

      if (sub.textContent !== text) sub.textContent = text;
      if (full && full !== text) sub.setAttribute('title', full); else sub.removeAttribute('title');

      tile.classList.toggle('has-result', !!rec);
      tile.classList.toggle('has-insight', insight.length > 0);
      tile.classList.toggle('is-fallback', state.unavailable && !rec);
      tile.classList.toggle('is-retry', retry);
      if (retry && inline) tile.setAttribute('title', tile.dataset.retryText || 'True Fit did not answer. Tap to try again.');
      else if (sizeForm >= 0) tile.setAttribute('title', action);
      else if (inline) tile.removeAttribute('title');
    });
    applyPick();
  }

  // Desktop capsule once a size is known: "Recommended: 9 - 9.5 US", or just the size
  // when the size label row has no room for the word.
  const inlineResultForms = (tile, rec) => [tile.dataset.resultLabelInline || 'Recommended: {size}', '{size}'].map((t) => t.replace('{size}', rec));

  // The capsule label never wraps, so a form that is too long for the row pushes the
  // row past its column, or squeezes "Size chart" / the chosen size onto two lines.
  // Each form is tried in turn and kept when the row stays whole and no taller than it
  // is with the shortest form. A capsule that is not laid out (mobile) takes the first
  // form. Returns the index of the form in use.
  function fitLabel(label, forms) {
    const row = label.closest('.form__label');
    const last = forms.length - 1;
    if (!row || label.clientWidth <= 1) { label.textContent = forms[0]; return 0; }
    label.textContent = forms[last];
    const height = row.getBoundingClientRect().height;
    for (let i = 0; i < last; i += 1) {
      label.textContent = forms[i];
      if (row.scrollWidth <= row.clientWidth + 1 && row.getBoundingClientRect().height <= height + 1) return i;
    }
    label.textContent = forms[last];
    return last;
  }

  /* ---- The recommended size box ------------------------------------------------ */

  const numbersOf = (text) => (String(text || '').match(/\d+(?:\.\d+)?/g) || []).map(Number);

  // Which of this product's sizes the recommendation points at. Sizes are matched on
  // their numbers: a box whose numbers are exactly the recommendation's wins (sandals
  // are sold as ranges, "7.5 - 8 US"), then, for a True Fit range against single-size
  // boxes ("9 - 9.5 US" vs "9 US"/"9.5 US"), the larger end, because Aetrex's own
  // guidance for in-between sizes is to size up. The range itself stays on the tile.
  function pickSize(recommendation) {
    const numbers = numbersOf(recommendation);
    if (!numbers.length) return null;
    const inputs = Array.from(document.querySelectorAll(SIZE_INPUT));
    const options = Array.from(document.querySelectorAll(SIZE_OPTION));
    const same = (a, b) => a.length === b.length && a.every((n, i) => n === b[i]);
    const tests = [
      (value) => same(numbersOf(value), numbers),
      (value) => numbersOf(value).includes(Math.max(...numbers)),
      (value) => numbersOf(value).includes(Math.min(...numbers)),
    ];
    const best = (list, read) => {
      for (const test of tests) {
        const hit = list.find((el) => test(read(el) || ''));
        if (hit) return hit;
      }
      return null;
    };
    const input = best(inputs, (el) => el.value || el.dataset.optionValue);
    const option = best(options, (el) => el.dataset.value);
    if (!input && !option) return null;
    return { value: (input && input.value) || (option && option.dataset.value) || '', input, option };
  }

  const markForms = (tile) => [
    (tile && tile.dataset.pickText) || 'Recommended for you',
    (tile && tile.dataset.pickShort) || 'Recommended',
    (tile && tile.dataset.pickMin) || 'Best fit',
  ];

  function pickMark(tile) {
    const mark = document.createElement('span');
    mark.className = 'tf-size-pick';
    mark.setAttribute('data-tf-pick', '');
    mark.setAttribute('aria-hidden', 'true');
    mark.appendChild(document.createTextNode(markForms(tile)[0]));
    return mark;
  }

  // Screen readers get the full wording inside the size's own label, whatever shortened
  // form the strip shows; the strip itself is decoration.
  function pickText(tile) {
    const text = document.createElement('span');
    text.className = 'sr-only';
    text.setAttribute('data-tf-pick-text', '');
    text.textContent = ' ' + markForms(tile)[0];
    return text;
  }

  // A narrow size box cannot hold the full mark, so the wording steps down until the
  // text fits its strip on one line (and, in the dropdown, until the row no longer
  // overflows). Re-run on resize.
  function fitMark(mark, tile, fits) {
    const text = mark.firstChild;
    for (const form of markForms(tile)) {
      text.nodeValue = form;
      if (fits()) return form;
    }
    return text.nodeValue;
  }

  // The strip runs along the inside of the box's bottom edge, so its bottom corners
  // follow the box's own radius, less the border it sits inside.
  function roundToBox(mark, label) {
    const cs = getComputedStyle(label);
    const inner = (radius, border) => Math.max(0, parseFloat(radius || '0') - parseFloat(border || '0')) + 'px';
    mark.style.borderBottomLeftRadius = inner(cs.borderBottomLeftRadius, cs.borderBottomWidth);
    mark.style.borderBottomRightRadius = inner(cs.borderBottomRightRadius, cs.borderBottomWidth);
  }

  // Mark the recommended size: its row in the mobile dropdown gets a pill; its box in
  // the desktop grid gets the wording for screen readers only (the capsule beside the
  // grid already shows the size), plus a strip along its bottom edge when the tile
  // asks for one (data-grid-mark="true"). Idempotent: marks are rebuilt from state each
  // time, so a re-rendered picker (variant change) gets them back and a changed
  // recommendation leaves nothing behind on the previous box.
  function applyPick() {
    document.querySelectorAll('[data-tf-pick], [data-tf-pick-text]').forEach((el) => el.remove());
    document.querySelectorAll('[data-tf-pick-host], .tf-size-pick-host').forEach((el) => {
      el.classList.remove('tf-size-pick-host');
      el.removeAttribute('aria-description');
      el.removeAttribute('data-tf-pick-host');
    });
    const rec = state.recommendation;
    const pick = rec ? pickSize(rec) : null;
    state.pick = pick;
    if (!pick) return;
    const tile = tiles()[0] || null;
    const fullText = markForms(tile)[0];
    if (pick.input) {
      const label = pick.input.closest('li') && pick.input.closest('li').querySelector('label');
      if (label) {
        if (tile && tile.dataset.gridMark === 'true') {
          const mark = pickMark(tile);
          label.appendChild(mark);
          label.classList.add('tf-size-pick-host');
          roundToBox(mark, label);
          // The strip is as wide as the box; the wording must fit it without clipping.
          if (label.clientWidth) fitMark(mark, tile, () => mark.scrollWidth <= mark.clientWidth + 1);
        }
        label.appendChild(pickText(tile));
        label.setAttribute('aria-description', fullText);
        label.setAttribute('data-tf-pick-host', '');
      }
    }
    if (pick.option) {
      const holder = pick.option.querySelector('.size-dropdown__option-label') || pick.option;
      const mark = pickMark(tile);
      holder.appendChild(mark);
      holder.appendChild(pickText(tile));
      pick.option.classList.add('tf-size-pick-host');
      pick.option.setAttribute('aria-description', fullText);
      pick.option.setAttribute('data-tf-pick-host', '');
      // In the dropdown the mark should share the line with the size; the row may wrap
      // as a last resort, so "fits" means "did not wrap under the size text".
      const value = holder.querySelector('.size-dropdown__option-value') || holder.firstElementChild;
      if (holder.clientWidth && value) fitMark(mark, tile, () => mark.getBoundingClientRect().top < value.getBoundingClientRect().bottom - 2);
    }
  }

  // Grid tiles keep the subline on one line: True Fit's segments are joined with " · "
  // and trailing segments are dropped until the line fits (the full text stays in the
  // title attribute). A subline that wraps (the mobile bar) always gets everything.
  function fitInsight(sub, segments) {
    // A second line that is not laid out for sighted readers (the capsule's is for screen
    // readers only) always gets the whole message.
    const single = sub.clientWidth > 1 && getComputedStyle(sub).whiteSpace === 'nowrap';
    for (let count = segments.length; count >= 1; count -= 1) {
      const text = segments.slice(0, count).join(' · ');
      if (!single || count === 1) return text;
      sub.textContent = text;
      if (sub.clientWidth === 0 || sub.scrollWidth <= sub.clientWidth + 1) return text;
    }
    return segments[0];
  }

  // The result line must never lose the size to an ellipsis: on a narrow tile the
  // shorter forms are tried until one fits on the line.
  function fitResult(sub, forms) {
    if (sub.clientWidth <= 1 || getComputedStyle(sub).whiteSpace !== 'nowrap') return forms[0];
    for (const text of forms) {
      sub.textContent = text;
      if (sub.clientWidth === 0 || sub.scrollWidth <= sub.clientWidth + 1) return text;
    }
    return forms[forms.length - 1];
  }

  // The bar's corners follow the mobile dropdown trigger above it, so whatever radius
  // the theme settings give it, the bar matches. The capsule keeps its own.
  function syncRadius() {
    tiles().forEach((tile) => {
      const reference = tile.classList.contains('tf-size-tile--bar')
        ? document.querySelector('.size-dropdown__trigger')
        : null;
      if (!reference) return;
      const radius = getComputedStyle(reference).borderRadius;
      if (radius && radius !== '0px') tile.style.setProperty('--tf-radius', radius);
    });
  }

  function setBusy(on) {
    state.busy = on;
    if (!on) state.loadingLabel = false;
    tiles().forEach((tile) => {
      tile.classList.toggle('is-busy', on);
      tile.setAttribute('aria-busy', on ? 'true' : 'false');
    });
    render();
  }

  // True Fit has nothing for this style: the tile becomes a size chart shortcut, or goes
  // away if the product has no size chart either.
  function markFallback() {
    trace('fallback');
    state.unavailable = true;
    const hasChart = !!sizeChartButton();
    if (!hasChart) tiles().forEach((tile) => { (tile.closest(TILE_ITEM) || tile).hidden = true; });
    render();
  }

  /* ---- True Fit DOM probes -------------------------------------------------- */

  const isShown = (el) => {
    if (!el || !el.getClientRects().length) return false;
    const style = getComputedStyle(el);
    return style.visibility !== 'hidden' && style.display !== 'none' && style.opacity !== '0';
  };

  // v1: the iframe is marked once the widget has drawn (data-widget-rendered) and is
  // always sized to its content, so a height is the reliable signal.
  function v1Iframe() {
    const host = placeholder();
    const iframe = host && host.querySelector('iframe');
    if (!iframe) return null;
    const rendered = iframe.dataset.widgetRendered === 'true' || parseFloat(iframe.style.height || '0') > 0;
    return rendered ? iframe : null;
  }

  // v2: the <tf-recommendation> component, its (open) shadow root and its control.
  function v2Component() {
    const host = placeholder();
    return (host && host.querySelector(V2_TAG)) || document.querySelector(V2_TAG) || null;
  }
  const v2Root = (el) => el.shadowRoot || el;
  const v2HasContent = (el) => !!v2Root(el).querySelector(':not(style):not(link)');

  // Our True Fit store key: the one layout/theme.liquid initialises True Fit with.
  const STORE_KEY = () => (window.AetrexTrueFit && window.AetrexTrueFit.storeKey) || 'aet';

  // The component reads its store from its own store-key attribute, so one mounted by a
  // foreign integration (store-key="atx" on the real page) asks a store that refuses
  // the request and never draws. Replace it with the same element wearing our key: the
  // element class is registered, so the fresh one boots at once, against our store.
  function repairComponent() {
    const component = v2Component();
    if (!component) return null;
    const want = STORE_KEY();
    const have = component.getAttribute('store-key');
    if (!have || have === want) return component;
    const host = component.parentNode || placeholder();
    if (!host) return component;
    const fresh = document.createElement(V2_TAG);
    for (const attr of Array.from(component.attributes)) {
      if (attr.name !== 'data-tf-tile-watched') fresh.setAttribute(attr.name, attr.value);
    }
    fresh.setAttribute('store-key', want);
    if (!fresh.getAttribute('store-style')) fresh.setAttribute('store-style', (host.dataset && host.dataset.styleid) || '');
    host.removeChild(component);
    host.appendChild(fresh);
    trace('repair', { from: have, to: want });
    return fresh;
  }

  // The control that opens the finder. The widget can hold more than one link (its logo
  // may link out to truefit.com), so the one whose text asks about size wins, then any
  // control that is not an outbound link, then whatever is first.
  function v2Button(el) {
    const root = v2Root(el);
    const controls = Array.from(root.querySelectorAll('button, a, [role="button"]'));
    const outbound = (c) => c.tagName === 'A' && /^https?:/i.test(c.getAttribute('href') || '');
    if (controls.length) {
      return controls.find((c) => CTA_TEXT.test(c.textContent))
        || controls.find((c) => !outbound(c))
        || controls[0];
    }
    // A recommendation strip with no explicit control is clickable as a whole in v2;
    // its first rendered element stands in for the control.
    const match = visibleText(root).match(SIZE_LINE);
    if (match && asSize(match[1])) return root.querySelector(':not(style):not(link)') || null;
    return null;
  }

  // Text content without the <style> blocks a shadow root carries.
  function visibleText(root) {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    let out = '';
    let node;
    while ((node = walker.nextNode())) {
      const el = node.parentElement;
      if (el && el.closest('style, script, noscript, template')) continue;
      out += ' ' + node.nodeValue;
    }
    return squash(out);
  }

  // The element that stands for the True Fit block: the app-block wrapper Shopify renders
  // around the placeholder (<div id="shopify-block-…" class="shopify-block shopify-app-block">),
  // as long as it holds nothing but True Fit — the placeholder, its own wrappers, True Fit's
  // scripts and custom elements. A wrapper that also holds other content is never used (on
  // the orthotics PDP the placeholder sat inside the container that holds the variant
  // selector, and hiding that container took the selector with it); the placeholder itself
  // stands in then. So nothing outside True Fit is ever hidden or made inert.
  function blockOf(host) {
    if (!host) return null;
    const wrapper = host.parentElement ? host.parentElement.closest('.shopify-app-block, [id^="shopify-block"]') : null;
    if (!wrapper || wrapper.contains(tiles()[0] || null)) return host;
    for (const el of wrapper.querySelectorAll('*')) {
      if (el === host || host.contains(el) || el.contains(host)) continue;
      if (/^(script|style|link|template|noscript)$/.test(el.localName)) continue;
      if (el.localName.startsWith('tf-') || /(^|\s)tfc?-/.test(el.getAttribute('class') || '') || /^tfc?-/.test(el.id || '')) continue;
      return host;
    }
    return wrapper;
  }

  // The block is always laid out and invisible; the armed class only records that a
  // finder is open, for anyone inspecting the DOM.
  function arm() {
    const block = quarantineBlock();
    noteStickyPositions();
    if (!block) return null;
    block.classList.add('tf-native-armed');
    state.armedBlock = block;
    return block;
  }

  function disarm() {
    if (state.armedBlock) state.armedBlock.classList.remove('tf-native-armed');
    state.armedBlock = null;
    if (state.closeTimer) { clearInterval(state.closeTimer); state.closeTimer = 0; }
    state.finderOpen = false;
    state.opening = false;
    releaseScrollLock();
    releaseStickyElements();
  }

  /* ---- Keeping the page still behind the finder ------------------------------ */

  // A short name for an element in the trace: tag#id.first-class.
  function labelOf(el) {
    try {
      const cls = typeof el.className === 'string' ? el.className.trim().split(/\s+/)[0] : '';
      return el.localName + (el.id ? '#' + el.id.slice(0, 60) : '') + (cls ? '.' + cls : '');
    } catch (error) { return '?'; }
  }

  // Where the page stands, for the trace: how far it is scrolled, what locks it, and
  // where the pinned elements sit (one that lost its footing shows up here): the product
  // column, the gallery, the announcement bar and the header, with the offset the bar
  // publishes for the header.
  function layoutSnapshot() {
    try {
      const body = document.body;
      const html = document.documentElement;
      const bodyStyle = getComputedStyle(body);
      const top = (el) => (el ? Math.round(el.getBoundingClientRect().top) : null);
      const position = (el) => (el ? getComputedStyle(el).position : null);
      const info = document.querySelector('product-info');
      const gallery = document.querySelector('.product__gallery-container');
      const bar = document.querySelector('[data-announcement-bar]');
      const barWrap = bar ? (bar.closest('.shopify-section') || bar) : null;
      const header = document.querySelector('.header-section');
      return {
        scrollY: Math.round(window.scrollY),
        bodyInline: body.style.cssText.slice(0, 160) || null,
        bodyOverflow: bodyStyle.overflow,
        bodyPosition: bodyStyle.position,
        htmlInline: html.style.cssText.slice(0, 160) || null,
        htmlOverflow: getComputedStyle(html).overflowX + '/' + getComputedStyle(html).overflowY,
        infoTop: top(info), infoPosition: position(info),
        galleryTop: top(gallery),
        barTop: top(barWrap), barPosition: position(barWrap), barHeight: barWrap ? Math.round(barWrap.getBoundingClientRect().height) : null,
        headerTop: top(header), headerPosition: position(header),
        headerOffset: getComputedStyle(html).getPropertyValue('--variant-bar-height').trim() || null,
        held: state.stickyBefore ? state.stickyBefore.filter((entry) => entry.held).map((entry) => labelOf(entry.el) + ':' + entry.held.dy) : null,
        relayed: !!state.lockRelayed,
      };
    } catch (error) { return null; }
  }

  // Is the page locked behind a modal right now, and how.
  function bodyLock() {
    const style = getComputedStyle(document.body);
    if (style.position === 'fixed') return 'fixed';
    if (style.overflow === 'hidden' || style.overflowY === 'hidden') return 'overflow';
    return null;
  }

  // MUI locks scrolling with overflow:hidden on <body>. That reaches the viewport only
  // when <html> leaves overflow alone (the body value is then applied to the viewport);
  // this theme clips <html> horizontally, so the lock lands on <body> itself: the page
  // still scrolls and every position:sticky element inside <body> loses its scrolling
  // container. Letting <html> go to overflow:visible for as long as the lock is on hands
  // the body value to the viewport, as the lock meant: the viewport stops scrolling, the
  // sticky elements keep their footing, and <body>'s own style is never touched — so the
  // lock is still there to be read, and its removal is seen. A lock set through a class
  // on <body> (v1 does that) works the same way. Done for any lock that lands while a
  // tap is in progress or the finder is open — before the finder is even recognised,
  // because the lock is set the moment it mounts and the page must never be drawn with
  // it on <body>. The scrollbar padding MUI adds to <body> stays where it is.
  function relayScrollLock() {
    const html = document.documentElement;
    if (state.lockRelayed || bodyLock() !== 'overflow') return;
    if (!state.finderOpen && !state.armedBlock && !state.opening) return;
    const computed = getComputedStyle(html);
    const saved = {};
    for (const prop of ['overflow', 'overflow-x', 'overflow-y']) saved[prop] = { value: html.style.getPropertyValue(prop), priority: html.style.getPropertyPriority(prop) };
    saved.wasVisible = computed.overflowX === 'visible' && computed.overflowY === 'visible';
    if (!saved.wasVisible) html.style.setProperty('overflow', 'visible', 'important');
    state.lockRelayed = saved;
    trace('scroll-lock', Object.assign({ relayed: true, needed: !saved.wasVisible }, layoutSnapshot() || {}));
  }

  function releaseScrollLock() {
    if (!state.lockRelayed) return;
    const html = document.documentElement;
    const saved = state.lockRelayed;
    if (!saved.wasVisible) {
      html.style.removeProperty('overflow');
      for (const prop of ['overflow', 'overflow-x', 'overflow-y']) {
        if (saved[prop] && saved[prop].value) html.style.setProperty(prop, saved[prop].value, saved[prop].priority);
      }
    }
    state.lockRelayed = null;
    trace('scroll-lock', { relayed: false });
  }

  // Everything on the page that is position:sticky right now, whatever it is called:
  // the product column, the gallery column, the pinned announcement bar, the header
  // wrapper, a theme sticky element. Only the outermost of a nested pair is kept (the
  // inner one moves with it). Hidden ones are left out.
  function stickyElements() {
    const picked = [];
    try {
      for (const el of document.body.querySelectorAll('*')) {
        if (el.namespaceURI !== 'http://www.w3.org/1999/xhtml') continue;
        if (getComputedStyle(el).position !== 'sticky') continue;
        if (!el.getClientRects().length) continue;
        if (picked.some((outer) => outer.contains(el))) continue;
        picked.push(el);
      }
    } catch (error) { /* detached */ }
    return picked;
  }

  // Just before the finder is opened: where each sticky element sits, and the scroll.
  function noteStickyPositions() {
    state.scrollBefore = window.scrollY;
    state.stickyBefore = stickyElements().map((el) => ({ el, top: el.getBoundingClientRect().top, held: null }));
  }

  // Sliding is done with the `translate` property where the browser has it, so a
  // transform the theme animates on the same element is left alone; `transform` otherwise.
  const SLIDE_PROP = (() => { try { return window.CSS && CSS.supports('translate', '0px 1px') ? 'translate' : 'transform'; } catch (error) { return 'transform'; } })();
  function slide(el, dy, held) {
    if (!dy) { el.style[SLIDE_PROP] = held.original || ''; return; }
    el.style[SLIDE_PROP] = SLIDE_PROP === 'translate' ? '0px ' + dy + 'px' : 'translateY(' + dy + 'px)';
  }

  // Once the finder has locked the page: any sticky element that is not where it was is
  // slid back there, which changes no layout. The slide is worked out afresh on every
  // call from the element's own position (its rectangle less what is already applied),
  // so one that finds its footing again — after the lock is relayed, say — is let go in
  // the same pass instead of being carried away by a stale slide. The page scroll is put
  // back too if True Fit moved it while opening (nothing of ours is on screen for it to
  // scroll to) and the lock has not frozen <body> in place.
  function holdStickyElements() {
    if (!state.stickyBefore) return;
    const lock = bodyLock();
    if (state.scrollBefore !== null && lock !== 'fixed' && Math.abs(window.scrollY - state.scrollBefore) > 2) {
      const from = Math.round(window.scrollY);
      window.scrollTo({ top: state.scrollBefore, left: window.scrollX, behavior: 'auto' });
      trace('scroll-restore', { from, to: Math.round(state.scrollBefore) });
    }
    const changed = [];
    for (const entry of state.stickyBefore) {
      if (!entry.el.isConnected) continue;
      const rect = entry.el.getBoundingClientRect();
      if (!rect.width && !rect.height) continue;
      // Vertical only: a sticky element that lost its footing moves up or down, while a
      // sideways shift is the scrollbar compensation that moves the whole page alike.
      const applied = entry.held ? entry.held.dy : 0;
      const dy = Math.round(entry.top - (rect.top - applied));
      if (Math.abs(dy) <= 1) {
        if (entry.held) { slide(entry.el, 0, entry.held); entry.held = null; changed.push({ el: labelOf(entry.el), dy: 0 }); }
        continue;
      }
      if (entry.held && entry.held.dy === dy) continue;
      if (!entry.held) entry.held = { original: entry.el.style[SLIDE_PROP], dy: 0 };
      entry.held.dy = dy;
      slide(entry.el, dy, entry.held);
      changed.push({ el: labelOf(entry.el), dy });
    }
    if (changed.length) trace('sticky-hold', { lock, moved: changed });
  }

  // Drop every slide. With keepBaseline the positions noted at the tap are kept, for a
  // lock that is lifted and set again while the finder is up; without it (the attempt
  // is over) they are forgotten too.
  function releaseStickyElements(keepBaseline) {
    if (!state.stickyBefore) return;
    let released = 0;
    for (const entry of state.stickyBefore) {
      if (!entry.held) continue;
      slide(entry.el, 0, entry.held);
      entry.held = null;
      released += 1;
    }
    if (!keepBaseline) {
      state.stickyBefore = null;
      state.scrollBefore = null;
    }
    if (released) trace('sticky-hold', { released });
  }

  // Relay first, then measure: with the lock on <html> the sticky elements are back in
  // place before anything is read, and a slide applied earlier is dropped in the same pass.
  function keepPageStill() {
    relayScrollLock();
    holdStickyElements();
  }

  // React the moment the lock is set or lifted, before anything is painted. <body>'s
  // style is the lock holder's own, untouched by the relay, so its going is seen here.
  if ('MutationObserver' in window) {
    new MutationObserver(() => {
      if (!state.finderOpen && !state.armedBlock && !state.opening) return;
      if (bodyLock()) { if (!state.finderOpen && modalOpen()) state.finderOpen = true; keepPageStill(); }
      else if (state.lockRelayed || (state.stickyBefore && state.stickyBefore.some((entry) => entry.held))) { releaseScrollLock(); releaseStickyElements(true); }
    }).observe(document.body, { attributes: true, attributeFilter: ['style', 'class'] });
  }

  // The block is normally laid out but invisible. Some visibility rules (an
  // IntersectionObserver that wants the block on screen, an opacity check) can keep
  // True Fit from drawing into it; revealing the block for a moment, in its own place
  // under the size picker, satisfies any of them. Concealed again as soon as the widget
  // has been opened or read. What is revealed is always the element that is marked (the
  // one the CSS collapses), never a fresh guess at which element that is.
  function reveal(why) {
    const block = quarantineBlock();
    if (!block || state.revealed) return;
    state.revealed = true;
    block.classList.add('tf-native-revealed');
    trace('reveal', { why });
  }

  // Every revealed element goes, not just the block as it is now: the product can have been
  // swapped, or the block re-marked, since the reveal, and a class left behind on any element
  // would leave True Fit's widget on show under the tile.
  function conceal() {
    document.querySelectorAll('.tf-native-revealed').forEach((el) => el.classList.remove('tf-native-revealed'));
    if (state.revealed) trace('conceal');
    state.revealed = false;
  }

  // True Fit's component decides what to draw when it is attached. Attached while the
  // block was hidden (the background warm-up does that), it can decide "nothing" and
  // never look again — on the real page it stayed blank through a reveal. Re-attaching
  // it while the block is visible makes it decide afresh; if even that draws nothing,
  // the element is removed and True Fit asked to mount a new one.
  function remount(component) {
    const parent = component && component.parentNode;
    if (!parent) return false;
    parent.removeChild(component);
    parent.appendChild(component);
    trace('remount');
    return true;
  }

  function recalculate(component) {
    if (component && component.parentNode) component.parentNode.removeChild(component);
    try { if (typeof window.tfcapi === 'function') window.tfcapi('calculate'); } catch (error) { /* True Fit's business */ }
    trace('recalculate');
  }

  const blankWidget = (component) => !!component && !v2HasContent(component) && !v2Button(component);

  // Give a blank widget its chances, cheapest first: re-attached while still hidden (no
  // flash if that is all it needed), then the block revealed, then re-attached while
  // visible, then replaced outright. Nothing is touched while True Fit is mid-request.
  // Returns the step taken this call, if any; `memo` keeps the timings across polls.
  function nudge(component, memo, now, quiet) {
    const since = (key) => now - (memo[key] || now);
    if (!memo.startedAt) memo.startedAt = now;
    if (!component) {
      // Library in but nothing mounted: show the block (True Fit may only mount what it
      // can see), then ask it to mount.
      if (!state.revealed) { reveal(memo.why || 'no-component'); memo.revealedAt = now; return 'reveal'; }
      if (!memo.recalculated && since('revealedAt') > NUDGE_STEP) { memo.recalculated = true; memo.recalculatedAt = now; recalculate(null); return 'recalculate'; }
      return null;
    }
    if (!blankWidget(component)) {
      // Text but no control: True Fit's "nothing for this style" note, or a skeleton. The
      // call is only made on a visible block, so show it; nothing else to nudge.
      if (!state.revealed) { reveal(memo.why || 'no-control'); memo.revealedAt = now; return 'reveal'; }
      return null;
    }
    if (quiet < NUDGE_QUIET) return null;
    if (!memo.remountedHiddenAt) { memo.remountedHiddenAt = now; remount(component); return 'remount'; }
    if (!state.revealed && since('remountedHiddenAt') > NUDGE_STEP) { reveal(memo.why || 'no-control'); memo.revealedAt = now; return 'reveal'; }
    if (state.revealed && !memo.remountedAt && since('revealedAt') > NUDGE_STEP) { memo.remountedAt = now; remount(component); return 'remount'; }
    if (memo.remountedAt && !memo.recalculated && since('remountedAt') > RECALC_AFTER) { memo.recalculated = true; memo.recalculatedAt = now; recalculate(component); return 'recalculate'; }
    return null;
  }

  // A dialog that is on screen and big enough to be a finder, not a tooltip or menu.
  // Any dialog in the root that is shown and of real size counts — not just the first
  // one in tree order, which can be a closed popover kept mounted ahead of the finder.
  function shownDialogIn(root, selector) {
    return Array.from(root.querySelectorAll(selector)).some((dialog) => {
      if (!isShown(dialog)) return false;
      const rect = dialog.getBoundingClientRect();
      return rect.width >= MIN_DIALOG_SIZE && rect.height >= MIN_DIALOG_SIZE;
    });
  }

  // A <body> child that is, or contains, a full-screen overlay: covering most of the
  // viewport itself, or holding a dialog in its light DOM. Being position:fixed is not
  // enough on its own: chat bubbles and cookie bars are fixed too, and a deferred script
  // can drop one on <body> in the same seconds the finder is opening.
  function looksLikeOverlay(node) {
    if (isShown(node)) {
      const rect = node.getBoundingClientRect();
      if (rect.width >= window.innerWidth * 0.6 && rect.height >= window.innerHeight * 0.6) return true;
    }
    return shownDialogIn(node, BODY_DIALOG);
  }

  // Everything that can be a True Fit finder on screen: v1's fixed container, an app
  // iframe, a dialog inside the v2 component, another True Fit custom element, a MUI
  // dialog anywhere on <body>, a dialog inside any shadow root hosted on <body> (v2 mounts
  // its finder that way, in a zero-size inline host that is never itself "visible"), or
  // any overlay appended to <body> after the open attempt.
  function modalOpen(bodyBefore) {
    if (document.body.classList.contains('tfc-show-modal')) return true;
    const container = document.getElementById('tfc-full-screen-container');
    if (container && getComputedStyle(container).display !== 'none') return true;

    if (Array.from(document.querySelectorAll('iframe[src*="truefitcorp"], iframe[src*="truefit.com"]'))
      .some((iframe) => !iframe.closest(PLACEHOLDER) && isShown(iframe))) return true;

    for (const el of document.querySelectorAll(V2_TAG)) {
      if (shownDialogIn(v2Root(el), V2_DIALOG)) return true;
    }

    if (Array.from(document.body.querySelectorAll('*'))
      .some((el) => el.localName.startsWith('tf-') && el.localName !== V2_TAG && isShown(el))) return true;

    // The theme has no MUI, so a shown MUI dialog anywhere on <body> is True Fit's.
    if (Array.from(document.body.querySelectorAll('.MuiDialog-root, .MuiModal-root, .MuiDrawer-root'))
      .some((el) => !el.closest(PLACEHOLDER) && isShown(el))) return true;

    for (const node of document.body.children) {
      if (node.shadowRoot && node.localName !== V2_TAG && shownDialogIn(node.shadowRoot, BODY_DIALOG)) return true;
    }

    if (bodyBefore) {
      for (const node of document.body.children) {
        if (!bodyBefore.has(node) && looksLikeOverlay(node)) return true;
      }
    }
    return false;
  }

  // Keep the block armed while the finder is up; collapse it and return focus after.
  // `seen` false means the finder was never detected: the block then stays armed for a
  // bounded time (in case it is open but unseen) and collapses without any fuss.
  function watchClose(bodyBefore, seen) {
    if (state.closeTimer) clearInterval(state.closeTimer);
    const startedAt = Date.now();
    let wasOpen = seen;
    state.closeTimer = setInterval(() => {
      const open = modalOpen(bodyBefore);
      const elapsed = Date.now() - startedAt;
      if (open) { wasOpen = true; keepPageStill(); }
      if (open && elapsed < 30 * 60 * 1000) return;
      if (!open && !wasOpen && elapsed < UNDETECTED_ARMED) return;
      disarm();
      if (wasOpen) {
        const tile = state.lastTile;
        if (tile && document.contains(tile)) tile.focus({ preventScroll: true });
      }
      [300, 1200, 3000].forEach((delay) => setTimeout(syncWidget, delay));
      if (wasOpen) setTimeout(peek, 500);
    }, CLOSE_POLL);
  }

  // After the finder closes True Fit redraws its strip with the answer. If the hidden
  // block stays blank (True Fit will not draw unseen), reveal it briefly, read it, hide it.
  function peek() {
    if (state.busy || state.revealed) return;
    const blank = (component) => !component || blankWidget(component);
    if (!blank(v2Component())) return;
    const startedAt = Date.now();
    const memo = { why: 'read-result' };
    const timer = setInterval(() => {
      const now = Date.now();
      const component = repairComponent();
      const drawn = !blank(component);
      if (!drawn) nudge(component, memo, now, quietFor(now));
      if (drawn || now - startedAt > PEEK_FOR || state.busy) {
        clearInterval(timer);
        if (drawn) { watchComponent(component); syncWidget(); }
        if (!state.busy) conceal();
      }
    }, 150);
  }

  /* ---- Widget text → tile ----------------------------------------------------- */

  // A value only counts as a size when it reads like one: a number, optionally after a
  // short system prefix ("US 9"), and short. State names ("nouser"), version tags ("v2")
  // and copy ("Loading…") all fail that test.
  const SIZE_KEYS = ['size', 'recommendedSize', 'recommendation', 'sizeLabel', 'displaySize', 'label'];
  const SIZE_SHAPE = /^(?:[a-z]{1,3}\s+)?\d/i;
  const SIZE_LINE = /size:?\s*(.+?)\s*(?:your recommendation|recommend|$)/i;

  const asSize = (text) => {
    const value = squash(text);
    return value && value.length <= 24 && SIZE_SHAPE.test(value) ? value : null;
  };

  function sizeFromState(value) {
    if (!value) return null;
    if (typeof value === 'string') return asSize(value);
    if (typeof value === 'object') {
      for (const key of SIZE_KEYS) {
        const found = sizeFromState(value[key]);
        if (found) return found;
      }
    }
    return null;
  }

  const LINE_BOX = /^(block|flex|grid|list-item|table.*)$/;
  const isLineBox = (el) => LINE_BOX.test(getComputedStyle(el).display);

  // The widget's visible text, one entry per rendered line: text nodes are grouped by the
  // nearest block-level ancestor, a pure call-to-action control ("What's my size?") and
  // the logo glyph are dropped, and whitespace is squashed. A control that carries the
  // message itself (the recommendation strip is one big button) keeps its text.
  // Markup-agnostic on purpose: True Fit can restructure its widget without the tile
  // losing a message.
  function v2Segments(component) {
    const root = v2Root(component);
    const control = v2Button(component);
    const controlText = control ? squash(control.textContent) : '';
    const skipControl = !!control && controlText.length <= 40 && CTA_TEXT.test(controlText);
    const groups = new Map();
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    let node;
    while ((node = walker.nextNode())) {
      const text = squash(node.nodeValue);
      const el = node.parentElement;
      if (!text || !el || el.closest('style, script, noscript, template, [aria-hidden="true"]')) continue;
      if (el.closest(V2_DIALOG)) continue; // an open finder drawn inside the component is not the widget's message
      if (skipControl && control.contains(el)) continue;
      if (!isShown(el)) continue;
      let group = el;
      while (group.parentElement && !isLineBox(group)) group = group.parentElement;
      groups.set(group, (groups.get(group) ? groups.get(group) + ' ' : '') + text);
    }
    const seen = new Set();
    const segments = [];
    for (let text of groups.values()) {
      text = text.replace(/^T\s+(?=\S)/, '').replace(/what'?s my size\??/i, '');
      text = squash(text);
      if (text.length < 2 || /^true ?fit$/i.test(text) || seen.has(text.toLowerCase())) continue;
      seen.add(text.toLowerCase());
      segments.push(text);
    }
    return segments;
  }

  // The collapsed block keeps real layout (see size-dropdown.css), so the widget can be
  // measured as it is; this wrapper stays as the single place that would change if it
  // ever had to be laid out on demand again.
  function withLayout(fn) {
    return fn();
  }

  // The True Fit block is collapsed by CSS (size-dropdown.css: [data-tf-native]) and must not
  // be reachable by keyboard or screen readers. The element is marked here, so the CSS and
  // this script always agree on which element that is. Applied at start and again whenever
  // the page changes (watchPage), before the change is painted. Only ever runs on a page that
  // has the tile. Returns the marked element, or null when there is nothing to hold.
  function quarantineBlock() {
    if (!tiles().length) return null;
    const block = blockOf(placeholder());
    if (state.block && state.block !== block) {
      // The block resolves to another element now (a new product's block, or a wrapper that
      // has since taken in something that is not True Fit's): the old one is let go.
      release(state.block);
      state.block = null;
    }
    if (!block || block.contains(tiles()[0] || null)) return null;
    if (!block.hasAttribute('data-tf-native')) block.setAttribute('data-tf-native', '');
    if (!block.hasAttribute('inert')) block.setAttribute('inert', '');
    if (block.getAttribute('aria-hidden') !== 'true') block.setAttribute('aria-hidden', 'true');
    state.block = block;
    return block;
  }

  function release(el) {
    if (!el || !el.isConnected) return;
    el.removeAttribute('data-tf-native');
    el.removeAttribute('inert');
    el.removeAttribute('aria-hidden');
    el.classList.remove('tf-native-revealed', 'tf-native-armed');
  }

  // The cheap check run on every change to the page: the marked element is still on the
  // page, still holds the placeholder and still carries its mark.
  function blockHeld() {
    const host = placeholder();
    if (!host) return true;
    const block = state.block;
    return !!block && block.isConnected && block.contains(host) && block.hasAttribute('data-tf-native');
  }

  // The recommendation from the retailer-facing state (tfcapi.widgetState, v1's
  // widgetResult) or from the rendered lines; everything else the widget says is insight.
  function readWidget() {
    let recommendation = sizeFromState(window.tfcapi && window.tfcapi.widgetState) || sizeFromState(state.widgetResult);
    const insight = [];
    const component = v2Component();
    if (component) {
      const segments = withLayout(() => v2Segments(component));
      for (const segment of segments) {
        const match = segment.match(SIZE_LINE);
        if (match && asSize(match[1])) { recommendation = recommendation || asSize(match[1]); continue; }
        if (/^your recommendation$/i.test(segment)) continue;
        insight.push(segment);
      }
      if (!recommendation) {
        const match = segments.join(' ').match(SIZE_LINE);
        if (match) recommendation = asSize(match[1]);
      }
    }
    return { recommendation, insight };
  }

  function syncWidget() {
    const { recommendation, insight } = readWidget();
    let changed = false;
    if (recommendation && recommendation !== state.recommendation) {
      state.recommendation = recommendation;
      state.known = true;
      try { localStorage.setItem(PROFILE_KEY, '1'); } catch (error) { /* storage blocked */ }
      announce('True Fit recommends size ' + recommendation + '. It is marked in the size list.');
      changed = true;
    }
    if (!recommendation && insight.join('\n') !== state.insight.join('\n')) {
      state.insight = insight;
      if (insight.length) announce('True Fit: ' + insight.join('. '));
      changed = true;
    }
    if (changed) render();
  }

  // Follow the v2 component: whenever it re-renders (after the finder closes, or when
  // a returning shopper's recommendation loads) the tile picks the change up.
  function watchComponent(component) {
    if (!component || component.dataset.tfTileWatched === 'true' || !('MutationObserver' in window)) return;
    component.dataset.tfTileWatched = 'true';
    let timer = 0;
    new MutationObserver(() => {
      clearTimeout(timer);
      timer = setTimeout(syncWidget, 150);
    }).observe(v2Root(component), { childList: true, subtree: true, characterData: true, attributes: true });
    syncWidget();
  }

  // Only used when the app block did not put a placeholder on the page.
  function ensurePlaceholder(productId) {
    let host = placeholder();
    if (host) return host;
    const wrapper = document.createElement('div');
    wrapper.className = 'tf-size-tile__host';
    wrapper.hidden = true;
    host = document.createElement('div');
    host.className = 'tfc-fitrec-product';
    host.dataset.styleid = String(productId || '');
    wrapper.appendChild(host);
    document.body.appendChild(wrapper);
    if (window.tfcapi && window.tfcapi.tfIntegrationLoaded) window.tfcapi('calculate');
    return host;
  }

  function openSizeChart() {
    const button = sizeChartButton();
    if (!button) return false;
    button.click();
    return true;
  }

  /* ---- Network activity + priority ------------------------------------------ */

  // How long True Fit has been silent: zero while any of its requests is still in
  // flight, otherwise the time since its last response. A page that is busy loading
  // slows True Fit down without making it silent, and this is what tells the two apart.
  const quietFor = (now) => (state.inflight > 0 ? 0 : now - state.lastActivity);

  function requestStarted() {
    state.inflight += 1;
    state.lastActivity = Date.now();
  }

  function requestSettled() {
    state.inflight = Math.max(0, state.inflight - 1);
    state.lastActivity = Date.now();
  }

  // Any True Fit traffic counts as progress: completed responses (PerformanceObserver),
  // fetches still pending (a thin wrapper around fetch, the same pattern half the apps on
  // the page use) and script chunks still downloading. Started once so nothing is missed.
  function observeActivity() {
    if ('PerformanceObserver' in window) {
      try {
        new PerformanceObserver((list) => {
          list.getEntries().forEach((entry) => {
            if (!TF_URL.test(entry.name)) return;
            state.lastActivity = Date.now();
            state.resources.push({ name: entry.name.replace(/^https?:\/\//, '').slice(0, 90), end: Math.round(entry.responseEnd), ms: Math.round(entry.duration), size: entry.transferSize || 0 });
            if (state.resources.length > 80) state.resources.shift();
          });
        }).observe({ entryTypes: ['resource'] });
      } catch (error) { /* activity heuristics degrade to the hard cap */ }
    }

    const nativeFetch = window.fetch;
    if (typeof nativeFetch === 'function') {
      window.fetch = function (input) {
        let url = '';
        try { url = typeof input === 'string' ? input : (input && input.url) || ''; } catch (error) { /* opaque input */ }
        const tracked = TF_URL.test(url);
        if (tracked) requestStarted();
        const result = nativeFetch.apply(this, arguments);
        if (tracked) Promise.resolve(result).then(requestSettled, requestSettled);
        return result;
      };
    }

    if ('MutationObserver' in window) {
      new MutationObserver((records) => {
        records.forEach((record) => {
          record.addedNodes.forEach((node) => {
            if (!node || node.nodeType !== 1 || node.localName !== 'script' || !TF_URL.test(node.src || '')) return;
            requestStarted();
            node.addEventListener('load', requestSettled, { once: true });
            node.addEventListener('error', requestSettled, { once: true });
          });
        });
      }).observe(document.documentElement, { childList: true, subtree: true });
    }
  }

  // The theme's deferred-script loaders (blocked-scripts-defer, third-party-defer and
  // Shopify's asyncLoad) release everything on the first interaction. When that
  // interaction is this tile, they honour this timestamp and wait, so True Fit is not
  // fighting a dozen analytics scripts for the connection.
  function holdThirdParty() {
    window.__holdDeferredScriptsUntil = Math.max(window.__holdDeferredScriptsUntil || 0, Date.now() + HOLD_THIRD_PARTY);
  }

  function startLoading() {
    const loader = window.AetrexTrueFit;
    if (!loader || typeof loader.load !== 'function') return null;
    if (!state.lastActivity) state.lastActivity = Date.now();
    return loader.load();
  }

  /* ---- Open flow ------------------------------------------------------------- */

  async function waitForWidget(host, attempt) {
    const startedAt = Date.now();
    let componentSeenAt = 0;
    let contentSeenAt = 0;
    let answeredAt = 0;
    let libSeen = false;
    let libSeenAt = 0;
    // Library already in before this tap (the warm-up ran): the widget has had its time.
    const warm = !!(window.tfcapi && (window.tfcapi.tfIntegrationLoaded || !window.tfcapi.q));
    const nudgeAfter = warm ? NUDGE_AFTER_WARM : NUDGE_AFTER;
    const memo = { why: 'no-control' };

    for (;;) {
      if (state.attempt !== attempt) throw new TileError('cancelled');
      const now = Date.now();

      // v1 only: once its library is in, listen for its answer so an empty widget is
      // recognised quickly instead of at the deadline.
      if (!state.listening && window.tfcapi && window.tfcapi.tfIntegrationLoaded) {
        state.listening = true;
        window.tfcapi('event', 'tfc-fitrec-product', 'widgetResult', (result) => {
          state.answered = true;
          state.widgetResult = result && typeof result === 'object' ? Object.values(result)[0] : result;
          syncWidget();
        });
      }

      if (!libSeen && window.tfcapi && (window.tfcapi.tfIntegrationLoaded || !window.tfcapi.q)) { libSeen = true; libSeenAt = now; trace('library'); }

      const iframe = v1Iframe();
      if (iframe) { trace('v1-iframe'); return { kind: 'v1', iframe }; }

      const component = repairComponent();
      const quiet = quietFor(now);
      if (component !== memo.lastComponent) { memo.lastComponent = component; if (component && componentSeenAt) { componentSeenAt = now; trace('component', { fresh: true }); } }
      // The library is in but no control has been drawn: give True Fit a real, visible
      // block to draw into, then a fresh attach, then a fresh component. Everything
      // below is judged against the visible block.
      if (libSeen && now - libSeenAt > nudgeAfter && !(component && v2Button(component)) && nudge(component, memo, now, quiet)) {
        // Something just changed for True Fit (block shown, component re-attached or
        // replaced); judge the result on the next pass, not this one.
        await wait(120);
        continue;
      }
      if (component) {
        const button = v2Button(component);
        if (button) { trace('control', { text: squash(button.textContent).slice(0, 80) }); return { kind: 'v2', component, button }; }
        if (!componentSeenAt) { componentSeenAt = now; trace('component'); }
        if (v2HasContent(component)) {
          // Text without a control is either a skeleton still loading or True Fit's
          // "nothing for this style" note. Only the latter is called: block visible, text
          // settled, and the network quiet. A slow page is not a missing recommendation.
          const text = withLayout(() => v2Segments(component)).join(' ');
          if (!contentSeenAt) { contentSeenAt = now; trace('content', { text: text.slice(0, 120) }); }
          if (text.length >= 8) {
            if (state.revealed && now - contentSeenAt > CONTENT_MIN_AGE && quiet > CONTENT_QUIET) throw new TileError('nodata', { text: text.slice(0, 120), age: now - contentSeenAt, quiet, definitive: true });
          } else if (memo.recalculated && now - componentSeenAt > EMPTY_MIN_AGE && quiet > EMPTY_QUIET) {
            throw new TileError('quiet', { skeleton: true, age: now - componentSeenAt, quiet });
          }
        } else if (memo.recalculated && now - componentSeenAt > EMPTY_MIN_AGE && quiet > EMPTY_QUIET) {
          throw new TileError('quiet', { age: now - componentSeenAt, quiet });
        }
      } else if (memo.recalculated && now - memo.recalculatedAt > EMPTY_MIN_AGE && quiet > EMPTY_QUIET) {
        // The blank component was replaced and True Fit mounted nothing in its place:
        // the same probable "no data" as a component that stays empty, not a stall.
        throw new TileError('quiet', { gone: true, age: now - memo.recalculatedAt, quiet });
      } else if (state.answered) {
        if (!answeredAt) answeredAt = now;
        // v1 answered explicitly, so an empty widget with an iframe is a definitive "no style".
        if (now - answeredAt > V1_ANSWER_GRACE) throw new TileError(host.querySelector('iframe') ? 'nodata' : 'quiet', { v1: true, definitive: true });
      } else if (now - startedAt > STALL_QUIET && quiet > STALL_QUIET) {
        throw new TileError('timeout', { stalled: true, quiet, library: libSeen });
      }

      if (now - startedAt > HARD_CAP) throw new TileError('timeout', { cap: true, library: libSeen, component: !!component });

      if (!state.loadingLabel && now - startedAt > LOADING_LABEL_AFTER) {
        state.loadingLabel = true;
        render();
      }
      await wait(120);
    }
  }

  async function open(tile) {
    if (state.busy) return;
    state.lastTile = tile;
    tile.dispatchEvent(new CustomEvent('truefit:tile', { bubbles: true, detail: { action: 'click' } }));

    if (state.unavailable) {
      openSizeChart();
      return;
    }

    if (typeof window.tfcapi !== 'function' || !window.AetrexTrueFit) {
      markFallback();
      openSizeChart();
      return;
    }

    const attempt = ++state.attempt;
    state.attemptStartedAt = Date.now();
    if (state.readyTimer) { clearInterval(state.readyTimer); state.readyTimer = 0; }
    trace('tap', { attempt, sinceLoad: Math.round(performance.now()), readyState: document.readyState });
    state.retry = false;
    setBusy(true);
    announce('Loading the size finder');
    holdThirdParty();

    try {
      const loading = startLoading();
      if (!loading) throw new TileError('failed', { reason: 'no loader' });
      loading.then((how) => trace('loader', { how }), (error) => trace('loader-error', { message: String(error && error.message) }));
      // Only a hard loader error (blocked, network) ends the attempt here; slowness is
      // the widget wait's business, so a successful load simply never settles this race.
      const loaderFailure = loading.then(() => new Promise(() => {}), () => { throw new TileError('failed'); });

      const host = (await waitFor(placeholder, 2000)) || ensurePlaceholder(tile.dataset.productId);
      if (state.attempt !== attempt) throw new TileError('cancelled');

      const widget = await Promise.race([waitForWidget(host, attempt), loaderFailure]);
      if (widget.kind === 'v2') watchComponent(widget.component);
      syncWidget();

      announce('Opening the True Fit size finder');
      const bodyBefore = new Set(document.body.children);

      state.opening = true;
      if (widget.kind === 'v1') {
        const styleId = host.dataset.styleid || host.id || tile.dataset.productId;
        noteStickyPositions();
        window.tfcapi('triggerOpen', { styleId: String(styleId) });
      } else {
        arm();
        widget.button.click();
      }
      trace('open-attempt', { kind: widget.kind });

      const opened = await waitFor(() => modalOpen(bodyBefore), OPEN_TIMEOUT);
      state.finderOpen = true;
      keepPageStill();
      trace(opened ? 'finder-open' : 'finder-unseen', layoutSnapshot() || {});
      conceal();
      watchClose(bodyBefore, !!opened);
    } catch (error) {
      disarm();
      conceal();
      const kind = error instanceof TileError ? error.kind : 'failed';
      const detail = (error && error.detail) || {};
      trace('exit', Object.assign({ kind, message: kind === 'failed' ? String(error && error.message) : undefined }, detail));
      if (kind === 'cancelled') return;
      if (kind === 'failed') {
        // Definitive: the library cannot load (blocked, offline). The size chart is the
        // sizing help there is, now and from here on.
        markFallback();
        if (openSizeChart()) announce('True Fit could not be loaded. Opening the size chart.');
      } else if (kind === 'nodata' && detail.definitive) {
        // Definitive: True Fit has nothing for this style.
        markFallback();
        if (openSizeChart()) announce('True Fit has no fit data for this style. Opening the size chart.');
      } else {
        // Silence or slowness is not an answer: nothing opens, the tile asks for another
        // tap, and True Fit keeps being watched so the next tap can open at once. Two
        // silent attempts on one style make it the size chart shortcut.
        if (kind === 'quiet' || kind === 'nodata') state.quietStrikes += 1;
        if (state.quietStrikes >= 2) {
          markFallback();
          announce('True Fit did not answer for this style. The size chart is available instead.');
        } else {
          state.retry = true;
          render();
          announce('True Fit did not answer. Tap to try again.');
        }
      }
      if (!state.unavailable) watchReady();
    } finally {
      if (state.attempt === attempt) setBusy(false);
    }
  }

  // After an attempt gave up, True Fit may still turn up a moment later. When it does,
  // the tile starts mirroring it right away, and the next tap opens without waiting.
  function watchReady() {
    if (state.readyTimer) return;
    const startedAt = Date.now();
    state.readyTimer = setInterval(() => {
      const component = repairComponent();
      if (component && (v2Button(component) || withLayout(() => v2Segments(component)).join(' ').length >= 8)) {
        clearInterval(state.readyTimer);
        state.readyTimer = 0;
        trace('ready-late', { after: Date.now() - startedAt });
        watchComponent(component);
        return;
      }
      if (Date.now() - startedAt > READY_WATCH) { clearInterval(state.readyTimer); state.readyTimer = 0; }
    }, 500);
  }

  // Cancel a pending attempt when the shopper moves on to something else.
  function cancelPending(event) {
    if (!state.busy) return;
    if (event.target.closest && event.target.closest(TILE)) return;
    trace('cancel', { target: event.target && event.target.localName });
    state.attempt += 1;
    conceal();
    setBusy(false);
  }

  document.addEventListener('click', (event) => {
    const tile = event.target.closest(TILE);
    if (!tile) return;
    event.preventDefault();
    open(tile);
  });

  // pointerdown lands a beat before click: start the download and ask the deferred
  // analytics scripts to wait. Capture phase, so this runs before the theme's own
  // first-interaction listeners on window.
  window.addEventListener('pointerdown', (event) => {
    if (event.target.closest && event.target.closest(TILE)) {
      if (!state.unavailable) { holdThirdParty(); startLoading(); }
    } else {
      cancelPending(event);
    }
  }, true);
  window.addEventListener('touchstart', (event) => {
    if (event.target.closest && event.target.closest(TILE) && !state.unavailable) holdThirdParty();
  }, { capture: true, passive: true });

  // The subline is fitted to the tile's width, which changes with the grid's breakpoints.
  let resizeTimer = 0;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => { syncRadius(); render(); }, 150);
  });

  // When the v1 modal closes True Fit focuses its (hidden) inline widget; put focus back
  // on the tile the shopper came from instead.
  if ('MutationObserver' in window) {
    new MutationObserver(() => {
      const isOpen = document.body.classList.contains('tfc-show-modal');
      if (state.modalWasOpen && !isOpen && state.lastTile && document.contains(state.lastTile)) {
        const tile = state.lastTile;
        setTimeout(() => tile.focus({ preventScroll: true }), 0);
      }
      state.modalWasOpen = isOpen;
    }).observe(document.body, { attributes: true, attributeFilter: ['class'] });
  }

  /* ---- Known shoppers ---------------------------------------------------------- */

  function isKnown() {
    try { if (localStorage.getItem(PROFILE_KEY) === '1') return true; } catch (error) { /* storage blocked */ }
    return !!tokenFromCookie();
  }

  // Load True Fit in the background once the page has settled (never competing with
  // the page itself): for everyone when WARM_AFTER_IDLE is on, so a tap a few seconds
  // into the visit opens the finder at once; otherwise only for shoppers True Fit already
  // knows, whose size then gets marked without a tap. No hold on the deferred analytics
  // scripts here, and nothing opens.
  function warmUp() {
    const known = isKnown();
    if ((!known && !WARM_AFTER_IDLE) || !window.AetrexTrueFit || !placeholder()) return;
    state.known = known;
    const start = () => {
      if (state.busy || state.unavailable || state.readyTimer) return;
      trace('warm-load', { known, sinceLoad: Math.round(performance.now()) });
      const loading = startLoading();
      if (loading) loading.catch((error) => trace('warm-load-error', { message: String(error && error.message) }));
      watchReady();
    };
    const schedule = () => setTimeout(() => {
      if ('requestIdleCallback' in window) window.requestIdleCallback(start, { timeout: 2000 });
      else start();
    }, WARM_DELAY);
    if (document.readyState === 'complete') schedule();
    else window.addEventListener('load', schedule, { once: true });
  }

  // Which product the page shows. A colour swatch swaps another product in without a page
  // load, and everything True Fit said belongs to the product it was said about.
  let colorwayHost = placeholder();
  function refreshColorway() {
    const host = placeholder();
    if (host !== colorwayHost) {
      colorwayHost = host;
      state.attempt += 1;
      disarm();
      conceal();
      Object.assign(state, {
        unavailable: false, answered: false, retry: false, quietStrikes: 0, nodataStrikes: 0,
        widgetResult: null, insight: [], pick: null, finderOpen: false, opening: false,
      });
      if (state.readyTimer) { clearInterval(state.readyTimer); state.readyTimer = 0; }
      state.productKey = (tiles()[0]?.dataset.productId || '') + '|' + (host?.dataset.styleid || '');
      quarantineBlock();
      syncRadius();
      setBusy(false);
      const api = window.tfcapi;
      if (host && typeof api === 'function' && (api.tfIntegrationLoaded || !api.q)) {
        try { api('calculate'); } catch (error) {}
        watchReady();
      }
    }
    render();
  }

  function productKey() {
    const tile = tiles()[0];
    const host = placeholder();
    return (tile ? tile.dataset.productId || '' : '') + '|' + (host ? host.dataset.styleid || '' : '');
  }

  // Another product was swapped in. What was learnt about the previous one goes (its
  // recommendation, insight, size-chart fallback, silent strikes), and an attempt still in
  // flight for it is dropped: nothing opens for a product that is no longer on the page.
  // True Fit, when it is on the page, draws into the new block by itself; the tile mirrors
  // it as soon as it does.
  function productSwitched(how) {
    const key = productKey();
    if (key === state.productKey) return;
    trace('product-switch', { how, from: state.productKey, to: key });
    state.productKey = key;
    state.attempt += 1;
    disarm();
    conceal();
    Object.assign(state, {
      unavailable: false, answered: false, retry: false, quietStrikes: 0, nodataStrikes: 0,
      widgetResult: null, recommendation: null, insight: [], pick: null, finderOpen: false, opening: false,
    });
    const watching = !!state.readyTimer;
    if (state.readyTimer) { clearInterval(state.readyTimer); state.readyTimer = 0; }
    quarantineBlock();
    syncRadius();
    setBusy(false);
    const library = !!(window.tfcapi && (window.tfcapi.tfIntegrationLoaded || !window.tfcapi.q));
    if (watching || library || state.lastActivity) watchReady();
  }

  // The theme replaces parts of the product page in place: the variant picker after every
  // pick, and the whole main-product section when a colour swatch switches to another product
  // (theme.js ProductColorSwatchHandler copies the innerHTML of every [id*="main"] element
  // from /products/<handle>, then fires product:switched). After a switch, product-info, the
  // tile and the True Fit block are all new elements, so the whole document is watched, never
  // an element found at load. Mutation callbacks run before the next paint: a new True Fit
  // block is collapsed before it can be seen. Marks and tile copy are state, so after any
  // re-render of the product info they are simply painted again.
  function watchPage() {
    if (!('MutationObserver' in window)) return;
    let timer = 0;
    new MutationObserver((records) => {
      if (!blockHeld()) {
        quarantineBlock();
        productSwitched('dom');
      }
      const scoped = !!document.querySelector('product-info');
      if (!records.some((record) => Array.from(record.addedNodes).some((node) => node.nodeType === 1 && !node.hasAttribute('data-tf-pick') && !node.hasAttribute('data-tf-pick-text') && !node.closest(PLACEHOLDER) && (!scoped || !!node.closest('product-info'))))) return;
      clearTimeout(timer);
      timer = setTimeout(() => { quarantineBlock(); syncRadius(); render(); }, 100);
    }).observe(document.documentElement, { childList: true, subtree: true });
    // The theme's own signal for the same switch; it fires before the mutation callback runs.
    document.addEventListener('product:switched', (event) => {
      if (event.detail?.source === 'instant-colorway') refreshColorway();
      else { quarantineBlock(); productSwitched('event'); }
    });
  }

  /* ---- Cart attribution ------------------------------------------------------ */

  // The True Fit app block ships its own /cart/add hook, but it goes through
  // tfcapi('getToken'), which only works once the library is loaded. This covers the
  // shoppers who never tap the tile this visit but engaged before: True Fit keeps its
  // token in the tfc-l cookie as {"k":{"v":token,"e":unixExpiry}}.
  function tokenFromCookie() {
    try {
      const entry = document.cookie.split(';').map((part) => part.trim()).find((part) => part.startsWith('tfc-l='));
      if (!entry) return null;
      const data = JSON.parse(decodeURIComponent(entry.slice('tfc-l='.length)));
      const token = data && data.k;
      if (!token || !token.v) return null;
      if (token.e && token.e < Math.floor(Date.now() / 1000)) return null;
      return token.v;
    } catch (error) {
      return null;
    }
  }

  function attachToken(token) {
    const formData = new FormData();
    formData.append('attributes[_tfcToken]', token);
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    fetch(root + 'cart/update.js', { method: 'POST', body: formData }).catch(() => {});
  }

  function observeCartAdds() {
    if (!('PerformanceObserver' in window)) return;
    try {
      const observer = new PerformanceObserver((list) => {
        list.getEntries().forEach((entry) => {
          const isRequest = entry.initiatorType === 'xmlhttprequest' || entry.initiatorType === 'fetch';
          if (!isRequest || !/\/cart\/add/.test(entry.name)) return;
          // Library loaded (v1 flag, or v2 has replaced the stub): the app block's hook handles it.
          const api = window.tfcapi;
          if (api && (api.tfIntegrationLoaded || (typeof api === 'function' && !api.q))) return;
          const token = tokenFromCookie();
          if (token) attachToken(token);
        });
      });
      observer.observe({ entryTypes: ['resource'] });
    } catch (error) {
      /* PerformanceObserver without resource support: attribution is skipped, nothing else is affected */
    }
  }

  quarantineBlock();
  state.productKey = productKey();
  syncRadius();
  render();
  observeActivity();
  observeCartAdds();
  watchPage();
  warmUp();

  trace('init', { sinceLoad: Math.round(performance.now()), readyState: document.readyState, placeholder: !!placeholder(), loader: !!window.AetrexTrueFit });
  window.AetrexTrueFitTile = { open, state, sync: syncWidget, refresh: refreshColorway, dump };
})();
